import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { SpecimenInstitutionSearch } from '../types/product.types';
import { toInstitutionFilterSearch, toInstitutionFilterValues } from '../utils/productFilters';

const route = getRouteApi('/_auth/product/specimen-institution');

const normalizeFilterValues = (values: Record<string, string>) =>
  toInstitutionFilterValues(toInstitutionFilterSearch(values));

export function useInstitutionFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const appliedValues = useMemo(() => toInstitutionFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toInstitutionFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const institutionSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): SpecimenInstitutionSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
    }),
    [search, defaultPeriod],
  );

  return { ...filters, ...institutionSearch, params };
}
